import * as React from 'react'

interface StoredValue<T> {
  __v?: string
  data: T | null | undefined
}

type UseLocalStorageStateReturn<T> = [T, React.Dispatch<React.SetStateAction<T>>, () => void]

interface UseLocalStorageStateOptions<T> {
  key: string
  initialValue: T | (() => T)
  version?: string
  serialize?: (value: StoredValue<T>) => string
  deserialize?: (raw: string) => StoredValue<T> | T | null | undefined
  onError?: (error: unknown) => void
}

function safeParse<T>(raw: string, fallback: T, onError?: (error: unknown) => void): T {
  try {
    return JSON.parse(raw) as T
  } catch (error) {
    if (onError) onError(error)
    return fallback
  }
}

function isStoredValue<T>(value: unknown): value is StoredValue<T> {
  return !!value && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, 'data')
}

function useLocalStorageState<T>({
  key,
  initialValue,
  version = '1',
  serialize = JSON.stringify,
  deserialize,
  onError,
}: UseLocalStorageStateOptions<T>): UseLocalStorageStateReturn<T> {
  const initial = React.useMemo<T>(
    () => (typeof initialValue === 'function' ? (initialValue as () => T)() : initialValue),
    [initialValue]
  )

  const read = React.useCallback((): T => {
    if (typeof window === 'undefined') return initial

    let raw: string | null = null

    // Safe read from localStorage
    try {
      raw = window.localStorage.getItem(key)
    } catch (error) {
      if (onError) onError(error)
      return initial
    }

    if (!raw) return initial

    const parsed = deserialize
      ? deserialize(raw)
      : safeParse<StoredValue<T> | T | null>(raw, {data: initial}, onError)

    const payload: StoredValue<T> = isStoredValue<T>(parsed)
      ? parsed
      : {__v: undefined, data: parsed as T | null | undefined}

    // Version mismatch -> ignore stored value
    if (payload.__v !== version) return initial

    return payload.data ?? initial
  }, [key, version, deserialize, initial, onError])

  const [state, setState] = React.useState<T>(() => read())

  React.useEffect(() => {
    if (typeof window === 'undefined') return

    const payload: StoredValue<T> = {__v: version, data: state}

    try {
      const serialized = serialize(payload)
      window.localStorage.setItem(key, serialized)
    } catch (error) {
      // Swallow errors by default so app does NOT crash.
      if (onError) {
        onError(error)
      } else {
        // Optional Debugging: uncomment for dev-time visibility without crashing
        // console.error(`Error setting localStorage key "${key}"`, error)
      }
    }
  }, [key, state, version, serialize, onError])

  const reset = React.useCallback(() => {
    try {
      const payload: StoredValue<T> = {__v: version, data: initial}
      const serialized = serialize(payload)
      window.localStorage.setItem(key, serialized)
      setState(initial)
    } catch (error) {
      if (onError) onError(error)
    }
  }, [initial, key, serialize, version, onError])

  return [state, setState, reset]
}

export default useLocalStorageState
